import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../service/tekkr-service";
import { useProject } from "../project";
import { ChatMetadata } from "../types/api-types";
import hash from "object-hash";
import { useOrg } from "../../auth/org-provider";
import { ChatType } from "tekkr-common/dist/model/chats/types";

export function useListProjectChatsQuery(options?: Omit<Options, "filter">) {
    const { project } = useProject();
    return useListChatsQuery({
        ...options,
        filter: {
            chatType: [ChatType.General, ChatType.Agent],
            scope: ["project", project.id],
        },
    });
}

interface Filter {
    chatType?: ChatType[];
    scope?: string[];
}

interface Options {
    sortBy?: "createdAt";
    filter?: Filter;
}

export function useListChatsQuery(options?: Options) {
    const org = useOrg();
    return useQuery({
        queryKey: ["chats", org.id, ...(options ? [hash(options)] : [])],
        queryFn: async () => {
            const res = await apiClient.listChats({
                query: {
                    type: options?.filter?.chatType?.join(",") ?? undefined,
                    scope: options?.filter?.scope?.join(",") ?? undefined,
                },
            });
            if (res.status !== 200) {
                throw new Error("Failed to fetch chats");
            }
            const chats = res.body.data.map((chat) => ({
                ...chat,
                createdAt: new Date(chat.createdAt),
            })) as ChatMetadata[];
            return sortChats(chats, options?.sortBy);
        },
    });
}

function sortChats(chats: ChatMetadata[], sortBy?: Options["sortBy"]) {
    if (sortBy === "createdAt") {
        return chats.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }
    return chats;
}

export function useInvalidateChatListQueries() {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries({ queryKey: ["chats"] });
    };
}
